/**
 * Voraussichtliches Lieferdatum auf der Produktseite (Block „Lieferzeit (Schätzung)“) und im Warenkorb.
 * Die Tage kommen aus dem Abschnitt „Lieferzeit-Einstellungen“ in der Footer-Gruppe (#lieferzeit-konfig).
 *
 * Das Datum wird im Browser berechnet, weil Shopify Seiten zwischenspeichert und
 * ein in Liquid berechnetes Datum veralten würde. Gerechnet wird in deutscher Zeit.
 * Versandtage: Mo–Fr (optional Sa) ohne Feiertage und ohne eingetragene Schließtage.
 * Zustelltage: Mo–Fr ohne Feiertage; Samstag je nach Einstellung nie, nur fürs früheste Datum oder immer.
 * Feiertage: bundesweit plus Rheinland-Pfalz (Fronleichnam, Allerheiligen).
 */

const MONATE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];
const TAG_MS = 24 * 60 * 60 * 1000;

/** Heutiges Datum in deutscher Zeit als UTC-Mitternacht (Zeitstempel). */
function heute() {
  const teile = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const wert = (typ) => Number(teile.find((teil) => teil.type === typ)?.value);
  return Date.UTC(wert('year'), wert('month') - 1, wert('day'));
}

/** @param {number} zeit */
function schluessel(zeit) {
  return new Date(zeit).toISOString().slice(0, 10);
}

/** Ostersonntag nach der Gaußschen Osterformel (gregorianisch). */
function ostersonntag(jahr) {
  const a = jahr % 19;
  const b = Math.floor(jahr / 100);
  const c = jahr % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return Date.UTC(jahr, monat - 1, tag);
}

/** @type {Map<number, Set<string>>} */
const feiertagsCache = new Map();

function feiertage(jahr) {
  let tage = feiertagsCache.get(jahr);
  if (!tage) {
    const ostern = ostersonntag(jahr);
    tage = new Set(
      [
        Date.UTC(jahr, 0, 1), // Neujahr
        Date.UTC(jahr, 4, 1), // Tag der Arbeit
        Date.UTC(jahr, 9, 3), // Tag der Deutschen Einheit
        Date.UTC(jahr, 10, 1), // Allerheiligen (RLP)
        Date.UTC(jahr, 11, 25), // 1. Weihnachtstag
        Date.UTC(jahr, 11, 26), // 2. Weihnachtstag
        // Karfreitag, Ostermontag, Christi Himmelfahrt, Pfingstmontag, Fronleichnam (RLP)
        ...[-2, 1, 39, 50, 60].map((abstand) => ostern + abstand * TAG_MS),
      ].map(schluessel)
    );
    feiertagsCache.set(jahr, tage);
  }
  return tage;
}

function istFeiertag(zeit) {
  return feiertage(new Date(zeit).getUTCFullYear()).has(schluessel(zeit));
}

/**
 * Liest Schließtage wie „24.12.2026“ oder „21.12.2026 - 03.01.2027“ (beliebig getrennt).
 * @param {string} text
 */
function leseSchliesstage(text) {
  /** @type {Set<string>} */
  const tage = new Set();
  const muster = /(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s*[-–]\s*(\d{1,2})\.(\d{1,2})\.(\d{4}))?/g;

  for (const treffer of text.matchAll(muster)) {
    const von = Date.UTC(+treffer[3], +treffer[2] - 1, +treffer[1]);
    const bis = treffer[4] ? Date.UTC(+treffer[6], +treffer[5] - 1, +treffer[4]) : von;
    for (let zeit = von; zeit <= bis && tage.size < 400; zeit += TAG_MS) {
      tage.add(schluessel(zeit));
    }
  }
  return tage;
}

/**
 * Zählt `anzahl` passende Tage ab `start` weiter. Bei 0 der nächste passende Tag (oder `start` selbst).
 * @param {number} start
 * @param {number} anzahl
 * @param {(zeit: number) => boolean} zaehlt
 */
function plusTage(start, anzahl, zaehlt) {
  let zeit = start;
  if (anzahl === 0) {
    while (!zaehlt(zeit)) zeit += TAG_MS;
    return zeit;
  }
  for (let gezaehlt = 0; gezaehlt < anzahl; ) {
    zeit += TAG_MS;
    if (zaehlt(zeit)) gezaehlt++;
  }
  return zeit;
}

/** „17. – 19. Oktober“ bzw. „30. Oktober – 3. November“ (wie bei Amazon) */
function formatiere(von, bis) {
  const a = new Date(von);
  const b = new Date(bis);
  // Geschütztes Leerzeichen, damit „19. Oktober“ nie umbricht.
  const tag = (datum) => `${datum.getUTCDate()}.`;
  const monat = (datum) => ` ${MONATE[datum.getUTCMonth()]}`;

  if (von === bis) return `${tag(a)}${monat(a)}`;
  if (a.getUTCMonth() === b.getUTCMonth()) return `${tag(a)} – ${tag(b)}${monat(b)}`;
  return `${tag(a)}${monat(a)} – ${tag(b)}${monat(b)}`;
}

/** [1, 2] → „1–2 Werktage“, [1, 1] → „1 Werktag“ */
function werktage([min, max]) {
  return min === max ? `${min} Werktag${min === 1 ? '' : 'e'}` : `${min}–${max} Werktage`;
}

/**
 * Einstellungen aus dem Abschnitt „Lieferzeit-Einstellungen“ (Footer-Gruppe).
 * @returns {{ bearbeitung: number[], laufzeit: number[], versandSamstag: boolean, samstag: 'nein' | 'frueh' | 'ja', schliesstage: string | null, warenkorb: boolean, warenkorbText: string | null } | null}
 */
function leseKonfig() {
  try {
    return JSON.parse(document.getElementById('lieferzeit-konfig')?.textContent || 'null');
  } catch {
    return null;
  }
}

/**
 * Berechnet die früheste und späteste Zustellung.
 * @param {{ bearbeitung: number[], laufzeit: number[], samstag: 'nein' | 'frueh' | 'ja', versandSamstag?: boolean, schliesstage: Set<string>, start?: number }} optionen
 */
export function lieferfenster({ bearbeitung, laufzeit, samstag, versandSamstag = false, schliesstage, start = heute() }) {
  const istVersandtag = (zeit) => {
    const wochentag = new Date(zeit).getUTCDay();
    return (
      wochentag !== 0 &&
      (versandSamstag || wochentag !== 6) &&
      !istFeiertag(zeit) &&
      !schliesstage.has(schluessel(zeit))
    );
  };
  /** @param {boolean} mitSamstag */
  const zustelltage = (mitSamstag) => (zeit) => {
    const wochentag = new Date(zeit).getUTCDay();
    return wochentag !== 0 && (mitSamstag || wochentag !== 6) && !istFeiertag(zeit);
  };

  const frueh = plusTage(
    plusTage(start, bearbeitung[0], istVersandtag),
    laufzeit[0],
    zustelltage(samstag === 'frueh' || samstag === 'ja')
  );
  const spaet = plusTage(plusTage(start, bearbeitung[1], istVersandtag), laufzeit[1], zustelltage(samstag === 'ja'));
  return { frueh, spaet, text: formatiere(frueh, spaet) };
}

class LieferzeitAnzeige extends HTMLElement {
  /** @type {Element | null} */
  #abschnitt = null;

  /** @type {string | undefined} */
  #ereignis;

  connectedCallback() {
    this.berechne();
    if (this.dataset.kontext !== 'produkt') return;

    // Bei Variantenwechsel ein-/ausblenden, je nachdem ob die neue Variante lieferbar ist.
    this.#abschnitt = this.closest('.shopify-section, dialog');
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        this.#ereignis = StandardEvents?.productSelect;
        if (this.#ereignis && this.isConnected) {
          this.#abschnitt?.addEventListener(this.#ereignis, this.#beiVariantenwechsel);
        }
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    if (this.#ereignis) this.#abschnitt?.removeEventListener(this.#ereignis, this.#beiVariantenwechsel);
  }

  /**
   * Solange [data-ziel] leer ist, blendet das CSS die Anzeige aus.
   * data-bereit sitzt auf einem Kind-Element, weil Horizon die Attribute des Elements selbst beim Neuzeichnen zurücksetzt.
   */
  berechne() {
    const ziel = this.querySelector('[data-ziel]');
    if (!ziel) return;

    const konfig = leseKonfig();
    const imWarenkorb = this.dataset.kontext === 'warenkorb';
    const inhalt = this.querySelector('.lieferzeit__text');

    if (!konfig || (imWarenkorb && !konfig.warenkorb)) {
      ziel.textContent = '';
      inhalt?.removeAttribute('data-bereit');
      return;
    }

    const textVorDatum = this.querySelector('[data-text]');
    if (imWarenkorb && konfig.warenkorbText && textVorDatum) {
      textVorDatum.textContent = konfig.warenkorbText;
    }

    const { text } = lieferfenster({
      bearbeitung: konfig.bearbeitung,
      laufzeit: konfig.laufzeit,
      samstag: konfig.samstag,
      versandSamstag: konfig.versandSamstag,
      schliesstage: leseSchliesstage(konfig.schliesstage || ''),
    });
    ziel.textContent = text;
    inhalt?.setAttribute('data-bereit', '');

    const hinweis = this.querySelector('[data-hinweis]');
    if (hinweis) {
      hinweis.textContent = `Bearbeitung ${werktage(konfig.bearbeitung)} · Zustellung ${werktage(konfig.laufzeit)}`;
    }
  }

  /** @param {any} event */
  #beiVariantenwechsel = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const neu = detail.html.querySelector(`lieferzeit-anzeige[data-block-id="${this.dataset.blockId}"]`);
        if (!neu) return;

        this.toggleAttribute('hidden', neu.hasAttribute('hidden'));
      })
      .catch(() => {});
  };
}

if (!customElements.get('lieferzeit-anzeige')) {
  customElements.define('lieferzeit-anzeige', LieferzeitAnzeige);

  // Theme-Editor: Nach Änderungen an den Lieferzeit-Einstellungen alle Anzeigen neu berechnen.
  document.addEventListener('shopify:section:load', () => {
    for (const anzeige of document.querySelectorAll('lieferzeit-anzeige')) {
      if (anzeige instanceof LieferzeitAnzeige) anzeige.berechne();
    }
  });
}
